import { useState, useEffect, useRef } from 'react';

interface MenuImageSlideshowProps {
  category: string | null;
}

const categoryVideos: Record<string, string[]> = {
  produse: [
    '/lovable-uploads/produse-video-1.mp4',
    '/lovable-uploads/produse-video-2.mp4',
  ],
  proiecte: [
    '/lovable-uploads/proiecte-video-1.mp4',
    '/lovable-uploads/proiecte-video-2.mp4',
  ],
  productie: [
    '/lovable-uploads/productie-video-1.mp4',
    '/lovable-uploads/productie-video-2.mp4',
  ],
  'unde-ne-gasesti': [
    '/lovable-uploads/unde-video-1.mp4',
    '/lovable-uploads/unde-video-2.mp4',
  ],
  contact: [
    '/lovable-uploads/contact-video-1.mp4',
  ],
};

const defaultVideos = [
  '/lovable-uploads/menu-mobile-video-1.mp4',
  '/lovable-uploads/menu-mobile-video-2.mp4',
];

export default function MenuImageSlideshow({ category }: MenuImageSlideshowProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedVideos, setLoadedVideos] = useState<Set<string>>(new Set());
  const videoRef = useRef<HTMLVideoElement>(null);

  const videos = category && categoryVideos[category] ? categoryVideos[category] : defaultVideos;
  const currentVideo = videos[currentIndex];

  // Reset index when category changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [category]);

  // Mark video as loaded once it has played at least metadata
  const handleLoadedData = () => {
    setLoadedVideos((prev) => {
      if (prev.has(currentVideo)) return prev;
      const next = new Set(prev);
      next.add(currentVideo);
      return next;
    });
  };

  // Advance to next video only after current one has had time to play
  useEffect(() => {
    if (videos.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % videos.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [videos.length]);

  return (
    <div className="relative w-full h-full overflow-hidden bg-muted">
      {/* Vignette overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-black/40 z-10 pointer-events-none" />

      {/* Single active video — swap source when index changes */}
      <video
        ref={videoRef}
        key={currentVideo}
        src={currentVideo}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
          loadedVideos.has(currentVideo) ? 'opacity-100' : 'opacity-0'
        }`}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onLoadedData={handleLoadedData}
        onCanPlay={handleLoadedData}
      />

      {/* Lightweight loading shimmer while video loads */}
      {!loadedVideos.has(currentVideo) && (
        <div className="absolute inset-0 bg-gradient-to-br from-muted via-background to-muted animate-pulse" />
      )}
    </div>
  );
}
